/**
 * Shaken Baby Syndrome Prevention Policy Tab
 * Full editable template matching the official PDF — view, edit, save, print, download, archive
 */
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Shield, Printer, Save, Archive, Trash2, Loader2, Download, History, X, Upload, FileText, Mail } from "lucide-react";
import jsPDF from "jspdf";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BLANK_STAFF = { name: "", position: "", date: "" };

export const ShakenBabyPolicyTab = () => {
  const { organization } = useAuth();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [savedPolicies, setSavedPolicies] = useState([]);
  const [uploadedDoc, setUploadedDoc] = useState(null); // {id, fileName, fileType}
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [noteToStaff, setNoteToStaff] = useState('');
  const [sending, setSending] = useState(false);
  const [showEmailPanel, setShowEmailPanel] = useState(false);
  const fileInputRef = useRef(null);
  const printRef = useRef(null);

  const [form, setForm] = useState({
    centerName: "",
    licenseNumber: "",
    centerAddress: "",
    directorName: "",
    effectiveDate: format(new Date(), 'yyyy-MM-dd'),
    lastReviewedDate: format(new Date(), 'yyyy-MM-dd'),
    staffAcknowledgements: [{ ...BLANK_STAFF }],
    adminName: "",
    archived: false,
  });

  useEffect(() => { fetchPolicies(); fetchStaff(); }, []);

  useEffect(() => {
    if (organization?.name) {
      setForm(prev => ({
        ...prev,
        centerName: prev.centerName || organization.name,
        centerAddress: prev.centerAddress || organization.address || "",
        licenseNumber: prev.licenseNumber || organization.licenseNumber || "",
        directorName: prev.directorName || "",
      }));
    }
  }, [organization]);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/compliance/shaken-baby-policy`);
      const all = res.data?.policies || [];
      setSavedPolicies(all);
      const latest = all.find(p => !p.archived) || all[0];
      if (latest) {
        setForm(prev => ({
          ...prev,
          ...latest,
          staffAcknowledgements: latest.staffAcknowledgements?.length
            ? latest.staffAcknowledgements
            : [{ ...BLANK_STAFF }],
        }));
      }
    } catch {} finally { setLoading(false); }
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${API}/staff-records`);
      const staff = Array.isArray(res.data) ? res.data : (res.data?.staff || res.data?.records || []);
      setStaffList(staff.filter(s => s.email).map(s => ({ name: s.name, email: s.email })));
    } catch {}
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowed.includes(file.type)) { toast.error("Please upload a PDF or image file"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("File must be under 15MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/compliance/shaken-baby-policy/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadedDoc(res.data);
      toast.success("Document uploaded — compliance updated");
      fetchPolicies();
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const handleSendEmail = async () => {
    if (selectedRecipients.length === 0) { toast.error("Select at least one recipient"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/compliance/shaken-baby-policy/email`, {
        recipients: selectedRecipients,
        docId: uploadedDoc?.id || null,
        centerName: form.centerName,
        noteToStaff: noteToStaff.trim(),
      });
      toast.success(res.data.message);
      setShowEmailPanel(false);
      setSelectedRecipients([]);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send email"); } finally { setSending(false); }
  };

  const toggleRecipient = (staff) => {
    setSelectedRecipients(prev =>
      prev.find(r => r.email === staff.email)
        ? prev.filter(r => r.email !== staff.email)
        : [...prev, staff]
    );
  };

  const setStaff = (idx, k, v) => {
    setForm(prev => {
      const arr = [...(prev.staffAcknowledgements || [])];
      arr[idx] = { ...arr[idx], [k]: v };
      return { ...prev, staffAcknowledgements: arr };
    });
  };

  const addStaff = () => setForm(p => ({ ...p, staffAcknowledgements: [...(p.staffAcknowledgements || []), { ...BLANK_STAFF }] }));
  const removeStaff = (idx) => setForm(p => ({ ...p, staffAcknowledgements: (p.staffAcknowledgements || []).filter((_, i) => i !== idx) }));

  const handleSave = async (archiveFlag = false) => {
    if (!form.centerName) { toast.error("Center name is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/compliance/shaken-baby-policy`, { ...form, archived: archiveFlag });
      toast.success(archiveFlag ? "Policy archived" : "Policy saved");
      fetchPolicies();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/compliance/shaken-baby-policy/${id}`);
      toast.success("Deleted");
      fetchPolicies();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const cn = form.centerName || organization?.name || 'Childcare Center';
    const pageW = 210; const margin = 15; const contentW = pageW - margin * 2;
    let y = 15;

    const addText = (text, x, size = 10, style = 'normal', color = [0,0,0], maxW = contentW) => {
      doc.setFontSize(size); doc.setFont('helvetica', style); doc.setTextColor(...color);
      const lines = doc.splitTextToSize(String(text || ''), maxW);
      lines.forEach(line => {
        if (y > 270) { doc.addPage(); y = 15; }
        doc.text(line, x, y);
        y += size * 0.45;
      });
      y += 1;
    };

    const addLine = (color = [200,200,200]) => {
      doc.setDrawColor(...color); doc.line(margin, y, pageW - margin, y); y += 3;
    };

    const addSection = (title, content) => {
      if (y > 250) { doc.addPage(); y = 15; }
      addText(title, margin, 10, 'bold', [26, 54, 93]);
      addLine([200, 200, 200]);
      if (Array.isArray(content)) {
        content.forEach(item => { addText('• ' + item, margin + 3, 9, 'normal', [50,50,50], contentW - 3); });
      } else {
        addText(content, margin, 9, 'normal', [50,50,50]);
      }
      y += 2;
    };

    // Title
    doc.setFillColor(153, 0, 0);
    doc.rect(margin, y, contentW, 14, 'F');
    doc.setFontSize(11); doc.setFont('helvetica','bold'); doc.setTextColor(255,255,255);
    doc.text('HOW TO IDENTIFY & PREVENT SHAKEN BABY SYNDROME,', pageW/2, y+5, {align:'center'});
    doc.text('ABUSIVE HEAD TRAUMA & CHILD MALTREATMENT POLICY', pageW/2, y+10, {align:'center'});
    y += 18;

    // Center info box
    doc.setFillColor(245,245,245); doc.setDrawColor(200,200,200);
    doc.rect(margin, y, contentW, 30, 'FD');
    y += 5;
    const infoItems = [
      ['Center Name:', cn], ['License #:', form.licenseNumber || ''], ['Address:', form.centerAddress || ''],
      ['Director/Administrator:', form.directorName || ''], ['Effective Date:', form.effectiveDate || ''], ['Last Reviewed:', form.lastReviewedDate || '']
    ];
    infoItems.forEach(([label, val]) => {
      doc.setFontSize(9); doc.setFont('helvetica','bold'); doc.setTextColor(0,0,0); doc.text(label, margin+3, y);
      doc.setFont('helvetica','normal'); doc.text(String(val), margin + 40, y);
      y += 4.5;
    });
    y += 4;

    // Sections
    addSection('1. PURPOSE', 'This policy ensures all staff members understand how to identify, prevent, and report Shaken Baby Syndrome (SBS), Abusive Head Trauma (AHT), and child maltreatment. The center is committed to providing a safe and protective environment for all children in its care.');

    addSection('2. DEFINITIONS', [
      'Shaken Baby Syndrome (SBS) — A serious brain injury caused by violently shaking an infant or young child.',
      'Abusive Head Trauma (AHT) — Injuries to a child\'s head or brain caused by intentional force or trauma.',
      'Child Maltreatment — Includes physical abuse, emotional abuse, sexual abuse, neglect, abandonment, or exploitation.'
    ]);

    // Zero tolerance
    doc.setFillColor(255,232,232); doc.setDrawColor(200,0,0);
    doc.rect(margin, y, contentW, 10, 'FD');
    doc.setFontSize(9); doc.setFont('helvetica','bold'); doc.setTextColor(150,0,0);
    doc.text('ZERO TOLERANCE: Staff may NEVER shake, strike, slap, or use excessive force toward a child.', margin+3, y+6);
    y += 14;

    addSection('4. SIGNS OF SBS OR AHT', ['Vomiting or seizures', 'Difficulty breathing', 'Unusual sleepiness or unresponsiveness', 'Poor feeding', 'Bruising around head/neck', 'Sudden behavioral changes']);
    addSection('5. SIGNS OF CHILD MALTREATMENT', ['Unexplained injuries or bruises', 'Poor hygiene or untreated medical needs', 'Fear of caregivers', 'Withdrawal, depression, or sudden aggression', 'Developmental regression']);
    addSection('6. PREVENTION STRATEGIES', ['Always support infants\' heads when lifting', 'NEVER shake a child for any reason', 'Use calming techniques when infants cry excessively', 'Ask a colleague for help when overwhelmed']);
    addSection('7. REPORTING REQUIREMENTS', 'All staff are MANDATED REPORTERS and must immediately report suspected abuse to the Director and ChildLine: 1-800-932-0313. Failure to report is a violation of state law and center policy.');
    addSection('8. STAFF TRAINING', 'All staff must complete training on SBS prevention, AHT awareness, identifying maltreatment, and mandated reporter responsibilities at hire and annually.');

    // Signature section
    if (y > 245) { doc.addPage(); y = 15; }
    y += 4;
    doc.setDrawColor(0); doc.line(margin, y, pageW - margin, y); y += 6;
    addText('DIRECTOR / ADMINISTRATOR CERTIFICATION', margin, 10, 'bold', [26,54,93]);
    addText('The Director/Administrator certifies that this policy has been reviewed with all staff members.', margin, 9, 'italic', [80,80,80]);
    y += 4;
    doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(0,0,0);
    doc.text('Name: ' + (form.adminName || ''), margin, y); y += 7;
    doc.text('Signature: ___________________________', margin, y);
    doc.text('Date: ' + (form.lastReviewedDate || ''), margin + 110, y); y += 10;

    // Footer
    doc.setFontSize(8); doc.setTextColor(150,150,150);
    doc.text(cn + ' | Policy Effective: ' + (form.effectiveDate || '') + ' | Last Reviewed: ' + (form.lastReviewedDate || ''), pageW/2, 287, {align:'center'});

    doc.save(`Shaken-Baby-Policy-${cn.replace(/\s+/g,'-')}.pdf`);
  };

  const handlePrint = () => {
    const token = localStorage.getItem('token');
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error("Allow popups to print"); return; }
    printWindow.document.write(buildPrintHTML());
    printWindow.document.close();
    printWindow.print();
  };

  const buildPrintHTML = () => {
    const cn = form.centerName || organization?.name || 'Childcare Center';
    return `<!DOCTYPE html><html><head><title>Shaken Baby Policy - ${cn}</title>
<style>
body{font-family:Arial,sans-serif;padding:36px;max-width:800px;margin:0 auto;font-size:11px;line-height:1.55;color:#000}
h1{text-align:center;font-size:15px;color:#900;margin-bottom:4px}
.sub{text-align:center;font-size:11px;font-weight:bold;color:#222;margin-bottom:16px}
.info-box{background:#f5f5f5;border:1px solid #ccc;padding:10px 14px;margin-bottom:14px;border-radius:4px}
.info-row{display:flex;gap:6px;margin-bottom:4px}.info-label{font-weight:bold;min-width:140px}
h2{font-size:12px;font-weight:bold;color:#1a365d;margin:14px 0 4px;text-transform:uppercase}
ul{margin:4px 0 8px 18px}li{margin-bottom:3px}
.zero-tol{background:#ffe8e8;border:1px solid #e00;padding:8px 12px;border-radius:4px;margin:8px 0}
table{width:100%;border-collapse:collapse;margin-top:8px}
th{background:#1a365d;color:#fff;padding:6px;text-align:left;font-size:10px}
.sig-section{margin-top:24px;border-top:2px solid #000;padding-top:16px}
.sig-row{display:flex;gap:20px;margin-top:14px}
.sig-field{flex:1}.sig-line{border-bottom:1px solid #000;height:20px;margin-top:4px}
.footer{margin-top:20px;text-align:center;font-size:9px;color:#888}
@media print{body{padding:14px}}
</style></head><body>
<h1>HOW TO IDENTIFY &amp; PREVENT SHAKEN BABY SYNDROME,<br/>ABUSIVE HEAD TRAUMA &amp; CHILD MALTREATMENT POLICY</h1>
<div class="sub">${cn}</div>
<div class="info-box">
<div class="info-row"><span class="info-label">Center Name:</span>${cn}</div>
<div class="info-row"><span class="info-label">License #:</span>${form.licenseNumber || '___________________'}</div>
<div class="info-row"><span class="info-label">Address:</span>${form.centerAddress || '___________________'}</div>
<div class="info-row"><span class="info-label">Director/Administrator:</span>${form.directorName || '___________________'}</div>
<div class="info-row"><span class="info-label">Policy Effective Date:</span>${form.effectiveDate || '___________________'}</div>
<div class="info-row"><span class="info-label">Last Reviewed/Updated:</span>${form.lastReviewedDate || '___________________'}</div>
</div>

<h2>1. Purpose</h2>
<p>This policy ensures all staff members understand how to identify, prevent, and report Shaken Baby Syndrome (SBS), Abusive Head Trauma (AHT), and child maltreatment. The center is committed to providing a safe and protective environment for all children in its care.</p>

<h2>2. Definitions</h2>
<ul>
<li><strong>Shaken Baby Syndrome (SBS)</strong> is a serious brain injury caused by violently shaking an infant or young child.</li>
<li><strong>Abusive Head Trauma (AHT)</strong> refers to injuries to a child's head or brain caused by intentional force or trauma.</li>
<li><strong>Child Maltreatment</strong> includes physical abuse, emotional abuse, sexual abuse, neglect, abandonment, or exploitation.</li>
</ul>

<h2>3. Policy Statement</h2>
<div class="zero-tol"><strong>ZERO TOLERANCE:</strong> This childcare center maintains a zero-tolerance policy for any form of abuse, neglect, or maltreatment of children. Staff members may NEVER shake, strike, slap, or use excessive physical force toward a child under any circumstances.</div>

<h2>4. Identifying Signs of SBS or AHT</h2>
<p>Staff must be alert to the following warning signs in infants and young children:</p>
<ul>
<li>Vomiting or seizures</li><li>Difficulty breathing or unusual breathing patterns</li>
<li>Unusual sleepiness or unresponsiveness</li><li>Poor feeding or sudden loss of appetite</li>
<li>Bruising around the head, neck, or eyes</li><li>Bulging fontanelle (soft spot on infant's head)</li>
<li>Sudden behavioral changes or irritability</li>
</ul>

<h2>5. Identifying Signs of Child Maltreatment</h2>
<p>Indicators may include physical, behavioral, or emotional signs:</p>
<ul>
<li>Unexplained injuries, bruises, or burns</li><li>Poor hygiene or untreated medical needs</li>
<li>Fear of specific caregivers or adults</li><li>Withdrawal, depression, or sudden aggression</li>
<li>Developmental regression or changes</li><li>Inappropriate sexual knowledge or behavior</li>
</ul>

<h2>6. Prevention Strategies</h2>
<ul>
<li>Always support infants' heads and necks when lifting or holding them</li>
<li>NEVER shake a child for any reason, including play</li>
<li>When infants cry excessively, check basic needs (hunger, diaper, comfort) and use calming techniques</li>
<li>Lay a baby down safely and step away briefly if feeling overwhelmed; ask a colleague for assistance</li>
<li>Never leave an infant alone with someone who cannot be trusted or who appears frustrated</li>
</ul>

<h2>7. Staff Stress Management</h2>
<p>Caring for infants can be stressful. Staff are encouraged to use appropriate coping strategies, request assistance from co-workers, take appropriate breaks, and communicate openly with supervisors if feeling overwhelmed. The center provides support and resources to help staff manage the demands of childcare.</p>

<h2>8. Reporting Requirements</h2>
<p>All staff are <strong>MANDATED REPORTERS</strong> and are legally required to report suspected child abuse or neglect immediately to:</p>
<ul>
<li>The Center Director or Administrator</li>
<li>The appropriate child protective services agency</li>
<li>ChildLine (Pennsylvania): <strong>1-800-932-0313</strong></li>
</ul>
<p>Failure to report suspected abuse is a violation of state law and center policy.</p>

<h2>9. Staff Training Requirements</h2>
<p>All staff must receive training on: Shaken Baby Syndrome prevention, abusive head trauma awareness, identifying child maltreatment, and mandated reporter responsibilities. Training must be completed at the time of hire and annually thereafter.</p>

<h2>10. Parent Education</h2>
<p>The center will provide parents and guardians with information about the dangers of shaking infants, safe soothing techniques, and available community resources. This education will be provided upon enrollment and made available throughout the year.</p>

<div class="sig-section">
<h2>Director / Administrator Certification</h2>
<p style="background:#e8f0fe;border:1px solid #3b5bdb;padding:10px;border-radius:4px;font-style:italic">
The Director/Administrator certifies that this policy has been reviewed with all staff members and that all staff understand their responsibilities regarding the identification, prevention, and reporting of Shaken Baby Syndrome, Abusive Head Trauma, and Child Maltreatment.
</p>
<div class="sig-row" style="margin-top:24px">
<div class="sig-field"><div style="font-weight:bold;font-size:10px">Director/Administrator Name</div><div class="sig-line" style="width:220px">${form.adminName || ''}</div></div>
<div class="sig-field"><div style="font-weight:bold;font-size:10px">Signature</div><div class="sig-line" style="width:220px"></div></div>
<div class="sig-field"><div style="font-weight:bold;font-size:10px">Date Reviewed with Staff</div><div class="sig-line" style="width:140px">${form.lastReviewedDate || ''}</div></div>
</div>
</div>
<div class="footer"><p>${cn} | Policy Effective: ${form.effectiveDate || ''} | Last Reviewed: ${form.lastReviewedDate || ''}</p></div>
</body></html>`;
  };

  if (loading) return (
    <Card><CardContent className="flex items-center justify-center py-8">
      <Loader2 className="w-6 h-6 animate-spin" />
    </CardContent></Card>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-red-600" />
            Shaken Baby / Abuse / Head Trauma Policy
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="border-green-400 text-green-700 hover:bg-green-50">
              <Download className="w-4 h-4 mr-1" />Download PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open('https://customer-assets.emergentagent.com/job_4e8e2bca-21e7-4a27-ac90-500c1c6fe36a/artifacts/ltywxsb8_csfxbmwo_How%20to%20Identify%20%26%20Prevent%20Shaken%20Baby%20Syndrome%2C%20Abusive%20Head%20Trama%20%26%20Child%20Maltreatment%20Policy.pdf', '_blank')}>
              <Download className="w-4 h-4 mr-1" />Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)}>
              <History className="w-4 h-4 mr-1" />History ({savedPolicies.length})
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowEmailPanel(!showEmailPanel)} className="border-blue-300 text-blue-700">
              <Mail className="w-4 h-4 mr-1" />Email Staff
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" />Print
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleSave(true)} disabled={saving} className="border-amber-400 text-amber-700 hover:bg-amber-50">
              <Archive className="w-4 h-4 mr-1" />Archive
            </Button>
            <Button size="sm" onClick={() => handleSave(false)} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* History panel */}
        {showHistory && (
          <div className="bg-gray-50 rounded-lg border p-3">
            <h4 className="font-semibold text-sm mb-2">Saved Policies</h4>
            {savedPolicies.length === 0
              ? <p className="text-xs text-gray-400 italic">No saved policies yet.</p>
              : <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {savedPolicies.map(p => (
                  <div key={p.id} className={`flex items-center justify-between px-3 py-2 rounded border bg-white text-xs ${p.archived ? 'opacity-60' : ''}`}>
                    <span className="cursor-pointer hover:text-blue-600 flex-1" onClick={() => setForm(prev => ({ ...prev, ...p, staffAcknowledgements: p.staffAcknowledgements?.length ? p.staffAcknowledgements : [{ ...BLANK_STAFF }] }))}>
                      {p.centerName} — {p.effectiveDate || 'No date'}
                      {p.archived && <span className="ml-2 text-amber-600 font-medium">[Archived]</span>}
                    </span>
                    <button onClick={() => handleDelete(p.id)} className="p-1 text-red-500 hover:bg-red-50 rounded ml-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            }
          </div>
        )}

        {/* Upload drag & drop */}
        <div className="border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 p-4"
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files?.[0]); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Upload className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">Upload Your Policy Document</p>
                <p className="text-xs text-gray-400">Drag & drop or click — PDF, JPG, PNG (Max 15MB)</p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4 mr-1" />Choose File</>}
            </Button>
          </div>
          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => handleFileUpload(e.target.files?.[0])} className="hidden" />
          {uploadedDoc && (
            <div className="mt-3 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-sm text-green-700 flex-1 truncate">{uploadedDoc.fileName}</span>
              <button onClick={() => { const t = localStorage.getItem('token'); window.open(`${API}/compliance/shaken-baby-policy/${uploadedDoc.id}/view?token=${t}`, '_blank'); }}
                className="text-xs text-blue-600 hover:underline flex-shrink-0">View</button>
              <button onClick={() => setUploadedDoc(null)} className="text-gray-400 hover:text-red-500 flex-shrink-0"><X className="w-4 h-4" /></button>
            </div>
          )}
        </div>

        {/* Preloaded Template Notice — informational only */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="preloaded-template-notice">
          <FileText className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            A preloaded Shaken Baby Syndrome Prevention Policy template is available below for your convenience. You may use the provided template or upload your own completed document.
          </p>
        </div>

        {/* Email staff panel */}
        {showEmailPanel && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-blue-800 flex items-center gap-2">
                <Mail className="w-4 h-4" />Email Policy to Staff
              </p>
              <button onClick={() => setShowEmailPanel(false)}><X className="w-4 h-4 text-gray-400 hover:text-gray-600" /></button>
            </div>
            {!uploadedDoc && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
                Tip: Upload a document above to include it as an attachment in the email.
              </p>
            )}
            <p className="text-xs text-blue-700 mb-2 font-medium">Select staff to receive this policy:</p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto mb-3">
              {staffList.length === 0
                ? <p className="text-xs text-gray-400 italic">No staff with email addresses found.</p>
                : staffList.map(s => (
                  <label key={s.email} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer border transition-all ${selectedRecipients.find(r => r.email === s.email) ? 'bg-blue-100 border-blue-400' : 'bg-white border-gray-200 hover:border-blue-300'}`}>
                    <input type="checkbox" checked={!!selectedRecipients.find(r => r.email === s.email)} onChange={() => toggleRecipient(s)} className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium flex-1">{s.name}</span>
                    <span className="text-xs text-gray-500">{s.email}</span>
                  </label>
                ))
              }
            </div>
            {/* Note to staff */}
            <div className="mt-2">
              <label className="text-xs font-medium text-blue-700 block mb-1">Note to Staff (optional)</label>
              <textarea
                value={noteToStaff}
                onChange={e => setNoteToStaff(e.target.value)}
                placeholder="Add a personal message to staff e.g. 'Please review this policy before our next staff meeting on Friday.'"
                rows={3}
                className="w-full border border-blue-300 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
              />
            </div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-500">{selectedRecipients.length} recipient{selectedRecipients.length !== 1 ? 's' : ''} selected</p>
              <Button size="sm" onClick={handleSendEmail} disabled={sending || selectedRecipients.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white">
                {sending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Sending...</> : <><Mail className="w-4 h-4 mr-1" />Send Policy</>}
              </Button>
            </div>
          </div>
        )}

        {/* Policy document — full editable template */}
        <div ref={printRef} className="border border-gray-200 rounded-xl overflow-hidden">

          {/* Document header */}
          <div className="bg-red-700 text-white text-center p-4">
            <p className="font-bold text-sm tracking-wide">HOW TO IDENTIFY &amp; PREVENT SHAKEN BABY SYNDROME,</p>
            <p className="font-bold text-sm tracking-wide">ABUSIVE HEAD TRAUMA &amp; CHILD MALTREATMENT POLICY</p>
          </div>

          <div className="p-5 space-y-5">
            {/* Facility Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div>
                <Label className="text-xs">Center Name</Label>
                <Input value={form.centerName} onChange={e => set('centerName', e.target.value)} className="mt-1 h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">License Number</Label>
                <Input value={form.licenseNumber} onChange={e => set('licenseNumber', e.target.value)} className="mt-1 h-8 text-sm" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Center Address</Label>
                <Input value={form.centerAddress} onChange={e => set('centerAddress', e.target.value)} className="mt-1 h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Director/Administrator</Label>
                <Input value={form.directorName} onChange={e => set('directorName', e.target.value)} className="mt-1 h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Policy Effective Date</Label>
                <Input type="date" value={form.effectiveDate} onChange={e => set('effectiveDate', e.target.value)} className="mt-1 h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Last Reviewed/Updated</Label>
                <Input type="date" value={form.lastReviewedDate} onChange={e => set('lastReviewedDate', e.target.value)} className="mt-1 h-8 text-sm" />
              </div>
            </div>

            {/* Policy Sections — read-only display */}
            {[
              { n: "1. PURPOSE", text: "This policy ensures all staff members understand how to identify, prevent, and report Shaken Baby Syndrome (SBS), Abusive Head Trauma (AHT), and child maltreatment. The center is committed to providing a safe and protective environment for all children in its care." },
              { n: "2. DEFINITIONS", list: ["Shaken Baby Syndrome (SBS) — A serious brain injury caused by violently shaking an infant or young child.", "Abusive Head Trauma (AHT) — Injuries to a child's head or brain caused by intentional force or trauma.", "Child Maltreatment — Includes physical abuse, emotional abuse, sexual abuse, neglect, abandonment, or exploitation."] },
              { n: "3. POLICY STATEMENT", alert: "ZERO TOLERANCE: This childcare center maintains a zero-tolerance policy for any form of abuse, neglect, or maltreatment of children. Staff members may NEVER shake, strike, slap, or use excessive physical force toward a child under any circumstances." },
              { n: "4. IDENTIFYING SIGNS OF SBS OR AHT", list: ["Vomiting or seizures", "Difficulty breathing or unusual breathing patterns", "Unusual sleepiness or unresponsiveness", "Poor feeding or sudden loss of appetite", "Bruising around the head, neck, or eyes", "Bulging fontanelle (soft spot on infant's head)", "Sudden behavioral changes or irritability"] },
              { n: "5. IDENTIFYING SIGNS OF CHILD MALTREATMENT", list: ["Unexplained injuries, bruises, or burns", "Poor hygiene or untreated medical needs", "Fear of specific caregivers or adults", "Withdrawal, depression, or sudden aggression", "Developmental regression or changes", "Inappropriate sexual knowledge or behavior"] },
              { n: "6. PREVENTION STRATEGIES", list: ["Always support infants' heads and necks when lifting or holding them", "NEVER shake a child for any reason, including during play", "When infants cry excessively, check basic needs and use calming techniques", "Lay a baby down safely and step away briefly if feeling overwhelmed; ask a colleague for assistance", "Never leave an infant with someone who cannot be trusted or appears frustrated"] },
              { n: "7. STAFF STRESS MANAGEMENT", text: "Caring for infants can be stressful. Staff are encouraged to use appropriate coping strategies, request assistance from co-workers, take appropriate breaks, and communicate openly with supervisors if feeling overwhelmed." },
              { n: "8. REPORTING REQUIREMENTS", mandate: true, text: "All staff are MANDATED REPORTERS and are legally required to immediately report suspected child abuse or neglect to: the Center Director, the appropriate child protective services agency, and ChildLine: 1-800-932-0313. Failure to report suspected abuse is a violation of state law and center policy." },
              { n: "9. STAFF TRAINING REQUIREMENTS", text: "All staff must receive training on: Shaken Baby Syndrome prevention, abusive head trauma awareness, identifying child maltreatment, and mandated reporter responsibilities. Training must be completed at hire and annually thereafter." },
              { n: "10. PARENT EDUCATION", text: "The center will provide parents and guardians with information about the dangers of shaking infants, safe soothing techniques, and available community resources upon enrollment and throughout the year." },
            ].map(s => (
              <div key={s.n}>
                <h4 className="text-xs font-bold text-blue-900 uppercase border-b border-blue-100 pb-1 mb-2">{s.n}</h4>
                {s.alert && <div className="bg-red-50 border border-red-300 rounded p-2 text-xs text-red-800 font-semibold">{s.alert}</div>}
                {s.text && <p className={`text-xs text-gray-700 ${s.mandate ? 'font-medium' : ''}`}>{s.text}</p>}
                {s.list && <ul className="text-xs text-gray-700 list-disc list-inside space-y-0.5">{s.list.map(i => <li key={i}>{i}</li>)}</ul>}
              </div>
            ))}

            {/* Administrator Review Certification */}
            <div className="border-t-2 border-gray-300 pt-4">
              <h4 className="text-xs font-bold text-blue-900 uppercase mb-2">Director / Administrator Certification</h4>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <p className="text-xs text-blue-800 italic">
                  The Director/Administrator certifies that this policy has been reviewed with all staff members and that all staff understand their responsibilities regarding the identification, prevention, and reporting of Shaken Baby Syndrome, Abusive Head Trauma, and Child Maltreatment.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Director/Administrator Name</Label>
                  <Input value={form.adminName || ''} onChange={e => set('adminName', e.target.value)} className="mt-1 h-8 text-sm" placeholder="Administrator name" />
                </div>
                <div>
                  <Label className="text-xs">Date Reviewed with Staff</Label>
                  <Input type="date" value={form.lastReviewedDate} onChange={e => set('lastReviewedDate', e.target.value)} className="mt-1 h-8 text-sm" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ShakenBabyPolicyTab;
